import { authorLabel } from 'components/views/company/outreach-template/message-thread/author-label';
import {
  AppNotification,
  NotificationKind,
  NotificationRecipientRole,
} from 'components/views/company/outreach-template/proposals/types';
import routes from 'routes';
import type { OutreachActivity_outreachActivity } from 'types/apollo/OutreachActivity';

const TITLE_BY_KIND: Record<string, string> = {
  [NotificationKind.ProposalSubmitted]: 'Proposal submitted',
  [NotificationKind.ProposalUpdated]: 'Proposal updated',
  [NotificationKind.ProposalApproved]: 'Proposal approved',
  [NotificationKind.ProposalApplied]: 'Proposal applied',
  [NotificationKind.ProposalRejected]: 'Proposal rejected',
  [NotificationKind.ProposalRemoved]: 'Proposal removed',
  [NotificationKind.ProposalStale]: 'Proposal stale',
  [NotificationKind.ProposalClosed]: 'Proposal closed',
  [NotificationKind.CommentAdded]: 'New comment',
  [NotificationKind.ChatMessage]: 'New message',
};

const buildLink = (e: OutreachActivity_outreachActivity): string | null => {
  if (!e.schedulerFlowId) return null;
  const isChat = e.refType === 'outreach_chat';

  // Client is campaign-keyed; &openChat opens the chat drawer, &proposalId the proposal's comments.
  if (e.recipientRole === 'client') {
    if (!e.campaignId) return null;
    const base = `${routes.campaign.resolve({
      campaignId: e.campaignId,
    })}/outreach-templates?templateId=${e.schedulerFlowId}`;
    if (isChat) return `${base}&openChat=1`;
    return e.proposalId ? `${base}&proposalId=${e.proposalId}` : base;
  }

  // Admin is company-keyed; chat passes the campaign short_id so the picker opens the right drawer.
  if (!e.companyId) return null;
  const base = routes.absolute.outreachTemplate.resolve({
    companyId: e.companyId,
    outreachTemplateId: e.schedulerFlowId,
  });
  if (isChat) {
    return e.campaignId
      ? `${base}?openChat=1&chatCampaignId=${e.campaignId}`
      : `${base}?openChat=1`;
  }
  return e.proposalId ? `${base}?proposalId=${e.proposalId}` : base;
};

export const toAppNotification = (
  e: OutreachActivity_outreachActivity,
  readIds: Set<string>,
  viewerIsInternal: boolean,
  currentUserSub: string,
): AppNotification => {
  // The actor is always the opposite side of the recipient. Reuse the chat/comment label rule so
  // a client sees "Aktify Team" for admin actors (real names only for same-company coworkers).
  const actorRole = e.recipientRole === 'admin' ? 'client' : 'admin';
  const actorName = authorLabel(
    { authorRole: actorRole, authorUserSub: e.actorUserSub ?? '', authorName: e.actorName ?? '' },
    currentUserSub,
    viewerIsInternal,
  );
  return {
    id: e.id,
    recipientUserSub: null,
    recipientRole: e.recipientRole as NotificationRecipientRole,
    actorUserSub: e.actorUserSub,
    actorName,
    companyName: e.companyName,
    kind: e.kind as NotificationKind,
    title: TITLE_BY_KIND[e.kind] ?? 'Update',
    body: e.body ?? '',
    refType: e.refType,
    refId: e.refId,
    link: buildLink(e),
    read: readIds.has(e.id),
    createdAt: e.createdAt,
  };
};
